import React, { useState, useEffect, useCallback } from 'react';
import { customerService, dashboardService } from '../services/api';
import { GlassButton, GlassInput, GlassCard, GlassPanel, GlassSelect, PageHeader } from '../components/GlassUI';
import { SEGMENTS, segmentMeta, MANUAL_TAG_MAP, MANUAL_TAG_ORDER } from '../constants/segments';
import { toCsv, downloadCsv, formatDate, todayStamp } from '../utils/download';
import {
  FaSearch, FaPhone, FaMapPin, FaShoppingBag, FaTimes, FaWhatsapp, FaShareAlt,
  FaEdit, FaTrash, FaSave, FaStar, FaUserPlus, FaUsers, FaDownload, FaGift
} from 'react-icons/fa';
import QrCode from '../components/QrCode';

interface CustomersPageProps {
  onNavigate?: (page: string, id?: string) => void;
}

const PROFESSION_OPTIONS = ['DOCTOR', 'LAWYER', 'TEACHER', 'ENGINEER', 'BUSINESS', 'GOVERNMENT', 'RETIRED', 'HOUSEWIFE', 'STUDENT', 'OTHER'];

export const CustomersPage: React.FC<CustomersPageProps> = ({ onNavigate }) => {
  const [customers, setCustomers] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // '' = all customers; otherwise a segment key from SEGMENTS.
  const [segmentFilter, setSegmentFilter] = useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editForm, setEditForm] = useState({
    name: '',
    cellNumber: '',
    whatsappNumber: '',
    address: '',
    profession: '',
    dateOfBirth: '',
    anniversaryDate: '',
    instagramHandle: '',
    referenceName: '',
    referencePhone: '',
  });

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      if (segmentFilter) {
        const response = await dashboardService.getSegmentCustomers(segmentFilter, page, 20);
        setCustomers(response.data.customers || []);
        setTotal(response.data.pagination?.total || 0);
      } else {
        const response = await customerService.getAll(page, 20, search);
        setCustomers(response.data.customers || []);
        setTotal(response.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search, segmentFilter]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = segmentFilter
        ? await dashboardService.getSegmentCustomersForExport(segmentFilter)
        : await customerService.getAllForExport(search);
      const rows: any[] = response.data.customers || [];
      const csv = toCsv(rows, [
        { header: 'Name', value: (r) => r.customerName || r.name },
        { header: 'Phone', value: (r) => r.cellNumber },
        { header: 'WhatsApp', value: (r) => r.whatsappNumber },
        { header: 'Location', value: (r) => r.location },
        { header: 'Profession', value: (r) => r.profession },
        { header: 'Purchases', value: (r) => r.purchaseCount ?? r.totalPurchases ?? 0 },
        { header: 'Lifetime Billing', value: (r) => r.lifetimeBilling ?? 0 },
        { header: 'Last Purchase', value: (r) => formatDate(r.lastPurchaseDate) },
        { header: 'Last Interaction', value: (r) => formatDate(r.lastInteractionDate) },
      ]);
      const name = segmentFilter ? `customers-${segmentFilter}-${todayStamp()}.csv` : `customers-all-${todayStamp()}.csv`;
      downloadCsv(name, csv);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Could not build the download. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const openEdit = (customer: any) => {
    setEditForm({
      name: customer.customerName || customer.name || '',
      cellNumber: customer.cellNumber || '',
      whatsappNumber: customer.whatsappNumber || '',
      address: customer.location || customer.address || '',
      profession: customer.profession || '',
      dateOfBirth: customer.dateOfBirth ? customer.dateOfBirth.split('T')[0] : '',
      anniversaryDate: customer.anniversaryDate ? customer.anniversaryDate.split('T')[0] : '',
      instagramHandle: customer.instagramHandle || '',
      referenceName: customer.referenceName || '',
      referencePhone: customer.referencePhone || '',
    });
    setSelectedCustomer(customer);
    setEditTags(manualTagsOf(customer));
    setShowEditModal(true);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const saveEdit = async () => {
    if (!editForm.name || !editForm.cellNumber) {
      alert('Name and phone number are required');
      return;
    }
    try {
      const payload: Record<string, any> = {
        name: editForm.name,
        cellNumber: editForm.cellNumber,
        whatsappNumber: editForm.whatsappNumber,
        address: editForm.address,
        profession: editForm.profession,
        dateOfBirth: editForm.dateOfBirth || undefined,
        anniversaryDate: editForm.anniversaryDate || undefined,
        instagramHandle: editForm.instagramHandle,
        referenceName: editForm.referenceName,
        referencePhone: editForm.referencePhone,
      };
      const res = await customerService.update(selectedCustomer.id, payload);
      const tagsRes = await customerService.setTags(selectedCustomer.id, editTags).catch(() => null);
      const manualTags = tagsRes?.data?.manualTags ?? editTags;
      const updated = { ...(res.data.customer || { ...selectedCustomer, ...payload }), manualTags };
      setCustomers(prev => prev.map(c => c.id === updated.id ? updated : c));
      setSelectedCustomer(updated);
      setShowEditModal(false);
      alert('Customer updated successfully');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update customer');
    }
  };

  const toggleEditTag = (tag: string) => {
    setEditTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const handleDelete = async (id: string) => {
    try {
      await customerService.delete(id);
      setCustomers(prev => prev.filter(c => c.id !== id));
      setDeleteConfirm(null);
      if (selectedCustomer?.id === id) setSelectedCustomer(null);
      alert('Customer deleted successfully');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete customer');
    }
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const whatsappLink = (phone: string) => `https://wa.me/${phone.replace(/[^0-9]/g, '')}`;
  const manualTagsOf = (c: any): string[] => (Array.isArray(c?.manualTags) ? c.manualTags : []);

  /** Toggle one manual tag on a card and persist immediately (optimistic). */
  const toggleCardTag = async (customer: any, tag: string) => {
    const current = manualTagsOf(customer);
    const next = current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag];
    const setLocalTags = (tags: string[]) =>
      setCustomers((prev) => prev.map((c) => (c.id === customer.id ? { ...c, manualTags: tags } : c)));
    setLocalTags(next);
    try {
      const res = await customerService.setTags(customer.id, next);
      setLocalTags(res?.data?.manualTags ?? next);
    } catch (error: any) {
      setLocalTags(current);
      alert(error.response?.data?.error || 'Failed to save tag');
    }
  };

  /** Manual tags to render, dropping VIP when Ideal is present (Ideal wins). */
  const displayManualTags = (c: any): string[] => {
    const tags = manualTagsOf(c);
    return tags.includes('IDEAL') ? tags.filter((t) => t !== 'VIP') : tags;
  };
  const isVIP = (c: any) =>
    manualTagsOf(c).includes('VIP') ||
    (c.lifetimeBilling || c.totalSpent || 0) > 500000;
  const isIdeal = (c: any) =>
    manualTagsOf(c).includes('IDEAL') ||
    ((c.purchaseCount ?? c.totalPurchases ?? 0) >= 2 && (c.lifetimeBilling || 0) > 500000);
  // Ideal is the stronger signal (repeat + recent + high value). When a customer
  // qualifies for both, show only the Ideal badge so it isn't duplicated.
  const showVipBadge = (c: any) => isVIP(c) && !isIdeal(c);

  const handleSegmentFilter = (key: string) => {
    setSegmentFilter(key);
    setPage(1);
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        title="Customers"
        subtitle={`${total} ${segmentFilter ? segmentMeta(segmentFilter).label.toLowerCase() : 'total customers'}`}
        icon={<FaUsers />}
        action={
          <GlassButton variant="primary" onClick={() => onNavigate?.('onboarding')} className="w-full sm:w-auto">
            <FaUserPlus /> New Customer
          </GlassButton>
        }
      />

      {/* Segment filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => handleSegmentFilter('')}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
            segmentFilter === ''
              ? 'bg-slate-900 text-white border-slate-900'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
        >
          All Customers
        </button>
        {SEGMENTS.map((seg) => (
          <button
            key={seg.key}
            onClick={() => handleSegmentFilter(seg.key)}
            title={seg.description}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              segmentFilter === seg.key
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {seg.shortLabel}
          </button>
        ))}
      </div>

      {/* Search + download */}
      <div className="flex gap-2 mb-6">
        <div className="flex-1 flex gap-2">
          <GlassInput
            type="text"
            placeholder="Search by name or phone..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <GlassButton variant="primary" onClick={handleSearch} className="shrink-0">
            <FaSearch />
          </GlassButton>
        </div>
        <GlassButton
          variant="secondary"
          className="shrink-0 flex items-center gap-2"
          onClick={handleExport}
          disabled={exporting || total === 0}
          title="Download this list as CSV"
        >
          <FaDownload /> <span className="hidden sm:inline">{exporting ? 'Preparing…' : 'Download'}</span>
        </GlassButton>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-slate-300 border-t-slate-900 rounded-full" />
        </div>
      )}

      {/* Customer Cards Grid */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {customers.map((customer: any) => (
            <GlassCard key={customer.id} className="flex flex-col hover:shadow-xl transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-slate-900 truncate">{customer.customerName || customer.name}</h3>
                    {showVipBadge(customer) && (
                      <span className="shrink-0 bg-yellow-100 text-yellow-800 p-1 rounded-full">
                        <FaStar size={10} />
                      </span>
                    )}
                  </div>
                  <p className="text-slate-500 text-sm flex items-center gap-1">
                    <FaPhone size={10} /> {customer.cellNumber}
                  </p>
                </div>
              </div>

              <div className="space-y-1.5 text-slate-600 text-sm mb-3 flex-1">
                {customer.location && (
                  <p className="flex items-center gap-1.5">
                    <FaMapPin size={10} className="text-green-400" /> {customer.location}
                  </p>
                )}
                <p className="flex items-center gap-1.5">
                  <FaShoppingBag size={10} className="text-yellow-400" /> {customer.purchaseCount ?? customer.totalPurchases ?? 0} purchases
                </p>
                {customer.totalSpent > 0 && (
                  <p className="text-xs text-slate-400">₹{customer.totalSpent.toLocaleString()} spent</p>
                )}
              </div>

              <div className="flex flex-wrap gap-1.5 mb-3">
                {customer.sourceType && (
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                    {customer.sourceType}
                  </span>
                )}
                {customer.isRepeatedCustomer && (
                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium">
                    Repeat
                  </span>
                )}
                {isIdeal(customer) && (
                  <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
                    <FaGift size={9} /> Ideal
                  </span>
                )}
                {customer.celebrationType && (
                  <span className="bg-pink-100 text-pink-800 px-2 py-0.5 rounded-full text-xs font-medium">
                    {customer.celebrationType === 'BIRTHDAY' ? '🎂 Birthday' : '💍 Anniversary'}
                  </span>
                )}
                {showVipBadge(customer) && (
                  <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs font-medium">
                    VIP
                  </span>
                )}
                {displayManualTags(customer).map((tag) => {
                  const meta = MANUAL_TAG_MAP[tag];
                  return (
                    <span
                      key={tag}
                      className={`${meta?.badgeClass || 'bg-indigo-100 text-indigo-700'} px-2 py-0.5 rounded-full text-xs font-medium`}
                    >
                      {meta?.shortLabel || tag}
                    </span>
                  );
                })}
              </div>

              {/* Manual customer tags - tap to apply/remove instantly. */}
              <div className="flex flex-wrap gap-1 mb-3 items-center">
                <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1">Tag:</span>
                {MANUAL_TAG_ORDER.map((tag) => {
                  const meta = MANUAL_TAG_MAP[tag];
                  const active = manualTagsOf(customer).includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleCardTag(customer, tag)}
                      title={meta?.description || tag}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${
                        active
                          ? `${meta?.badgeClass || 'bg-indigo-100 text-indigo-700'} border-transparent`
                          : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {meta?.shortLabel || tag}
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-2 mt-auto">
                <GlassButton
                  variant="secondary"
                  className="flex-1 text-sm"
                  onClick={() => setSelectedCustomer(customer)}
                >
                  View
                </GlassButton>
                <a
                  href={whatsappLink(customer.whatsappNumber || customer.cellNumber)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 rounded-lg font-semibold transition-all bg-green-500 text-white hover:bg-green-600 flex items-center justify-center"
                >
                  <FaWhatsapp size={14} />
                </a>
                <GlassButton
                  variant="secondary"
                  className="text-sm px-3"
                  onClick={() => openEdit(customer)}
                >
                  <FaEdit size={12} />
                </GlassButton>
                <GlassButton
                  variant="secondary"
                  className="text-sm px-3 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setDeleteConfirm(customer.id)}
                >
                  <FaTrash size={12} />
                </GlassButton>
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      {!loading && customers.length === 0 && (
        <GlassCard className="text-center py-12">
          <p className="text-slate-400 text-lg">No customers found</p>
        </GlassCard>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-center items-center gap-2 mt-8">
          <GlassButton
            onClick={() => setPage(Math.max(1, page - 1))}
            variant="secondary"
            disabled={page === 1}
          >
            Previous
          </GlassButton>
          <GlassCard className="px-4 py-2 text-slate-900 text-sm">
            Page {page} of {Math.ceil(total / 20)}
          </GlassCard>
          <GlassButton
            onClick={() => setPage(page + 1)}
            variant="secondary"
            disabled={page * 20 >= total}
          >
            Next
          </GlassButton>
        </div>
      )}

      {/* Customer Detail Modal */}
      {selectedCustomer && !showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedCustomer(null)}>
          <GlassCard className="max-w-lg w-full max-h-[85vh] flex flex-col !p-0 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold text-slate-900">{selectedCustomer.customerName || selectedCustomer.name}</h2>
                  {isIdeal(selectedCustomer) && (
                    <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1">
                      <FaGift size={10} /> Ideal
                    </span>
                  )}
                  {showVipBadge(selectedCustomer) && (
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1">
                      <FaStar size={10} /> VIP
                    </span>
                  )}
                </div>
                <p className="text-slate-500 text-sm">{selectedCustomer.cellNumber}</p>
              </div>
              <button onClick={() => setSelectedCustomer(null)} className="text-slate-400 hover:text-slate-600">
                <FaTimes size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0 px-5 py-4">
            <div className="space-y-3 text-slate-700 mb-4">
              {selectedCustomer.whatsappNumber && (
                <p><strong>WhatsApp:</strong> {selectedCustomer.whatsappNumber}</p>
              )}
              {selectedCustomer.secondaryNumber && (
                <p><strong>Secondary:</strong> {selectedCustomer.secondaryNumber}</p>
              )}
              {(selectedCustomer.location || selectedCustomer.address) && (
                <p><strong>Location:</strong> {selectedCustomer.location || selectedCustomer.address}</p>
              )}
              {selectedCustomer.profession && (
                <p><strong>Profession:</strong> {selectedCustomer.profession}</p>
              )}
              {selectedCustomer.instagramHandle && (
                <p><strong>Instagram:</strong> {selectedCustomer.instagramHandle}</p>
              )}
              {selectedCustomer.dateOfBirth && (
                <p><strong>DOB:</strong> {new Date(selectedCustomer.dateOfBirth).toLocaleDateString()}</p>
              )}
              {selectedCustomer.anniversaryDate && (
                <p><strong>Anniversary:</strong> {new Date(selectedCustomer.anniversaryDate).toLocaleDateString()}</p>
              )}
              {selectedCustomer.referenceName && (
                <p><strong>Referred by:</strong> {selectedCustomer.referenceName} {selectedCustomer.referencePhone ? `(${selectedCustomer.referencePhone})` : ''}</p>
              )}
              <p><strong>Purchases:</strong> {selectedCustomer.totalPurchases || 0}</p>
              <p><strong>Total Spent:</strong> ₹{(selectedCustomer.totalSpent || 0).toLocaleString()}</p>
              {selectedCustomer.sourceType && (
                <p><strong>Source:</strong> {selectedCustomer.sourceType}</p>
              )}
            </div>

            {/* Self-Service QR */}
            <GlassCard className="mb-4 bg-purple-50 border-purple-200">
              <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                <FaShareAlt className="text-purple-500" /> Share with Customer
              </h3>
              <p className="text-sm text-slate-600 mb-3">
                Let customers fill/verify their details via this link:
              </p>
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="text"
                  readOnly
                  value={`${window.location.origin}${process.env.PUBLIC_URL || ''}/customer-form?ref=${selectedCustomer.id}`}
                  className="flex-1 px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg text-slate-900"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <GlassButton
                  variant="primary"
                  className="text-sm px-3 shrink-0"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}${process.env.PUBLIC_URL || ''}/customer-form?ref=${selectedCustomer.id}`);
                    alert('Link copied!');
                  }}
                >
                  Copy
                </GlassButton>
              </div>
              <div className="flex justify-center">
                <QrCode
                  value={`${window.location.origin}${process.env.PUBLIC_URL || ''}/customer-form?ref=${selectedCustomer.id}`}
                  size={128}
                  fileName={`customer-${(selectedCustomer.customerName || selectedCustomer.id).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-qr`}
                />
              </div>
            </GlassCard>
            </div>

            <div className="flex flex-wrap gap-2 px-5 py-4 border-t border-slate-100 shrink-0">
              <a
                href={whatsappLink(selectedCustomer.whatsappNumber || selectedCustomer.cellNumber)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-4 py-2 rounded-lg font-semibold text-center bg-green-500 text-white hover:bg-green-600 text-sm"
              >
                <FaWhatsapp className="inline mr-1" /> WhatsApp
              </a>
              <GlassButton variant="secondary" className="flex-1 text-sm" onClick={() => openEdit(selectedCustomer)}>
                <FaEdit className="mr-1" /> Edit
              </GlassButton>
              <GlassButton
                variant="secondary"
                className="text-sm text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => { setDeleteConfirm(selectedCustomer.id); setSelectedCustomer(null); }}
              >
                <FaTrash />
              </GlassButton>
            </div>
          </GlassCard>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowEditModal(false)}>
          <GlassPanel className="max-w-lg w-full max-h-[85vh] flex flex-col overflow-hidden !p-0" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start px-6 py-4 border-b border-slate-100 shrink-0">
              <h2 className="text-2xl font-bold text-slate-900">Edit Customer</h2>
              <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-slate-600">
                <FaTimes size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4">
            <div className="space-y-4">
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Name *</label>
                <GlassInput name="name" value={editForm.name} onChange={handleEditChange} />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Phone *</label>
                <GlassInput name="cellNumber" value={editForm.cellNumber} onChange={handleEditChange} />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">WhatsApp</label>
                <GlassInput name="whatsappNumber" value={editForm.whatsappNumber} onChange={handleEditChange} />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Address</label>
                <GlassInput name="address" value={editForm.address} onChange={handleEditChange} />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Instagram</label>
                <GlassInput name="instagramHandle" value={editForm.instagramHandle} onChange={handleEditChange} />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Date of Birth</label>
                <GlassInput type="date" name="dateOfBirth" value={editForm.dateOfBirth} onChange={handleEditChange} />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Anniversary</label>
                <GlassInput type="date" name="anniversaryDate" value={editForm.anniversaryDate} onChange={handleEditChange} />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Profession</label>
                <GlassSelect name="profession" value={editForm.profession} onChange={handleEditChange}>
                  <option value="">Select</option>
                  {PROFESSION_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </GlassSelect>
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Reference Name</label>
                <GlassInput name="referenceName" value={editForm.referenceName} onChange={handleEditChange} />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Reference Phone</label>
                <GlassInput name="referencePhone" value={editForm.referencePhone} onChange={handleEditChange} />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-2 text-sm">Manual Customer Type</label>
                <p className="text-xs text-slate-500 mb-2">
                  Mark this customer by hand. These are added on top of the automatic
                  VIP / Ideal rules and stay until you remove them.
                </p>
                <div className="flex flex-wrap gap-2">
                  {MANUAL_TAG_ORDER.map((tag) => {
                    const meta = MANUAL_TAG_MAP[tag];
                    const active = editTags.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleEditTag(tag)}
                        title={meta?.description || tag}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                          active
                            ? `${meta?.badgeClass || 'bg-indigo-100 text-indigo-700'} border-transparent`
                            : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {meta?.shortLabel || tag}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            </div>

            <div className="flex gap-3 px-6 py-4 border-t border-slate-100 shrink-0">
                <GlassButton variant="secondary" className="flex-1" onClick={() => setShowEditModal(false)}>Cancel</GlassButton>
                <GlassButton variant="primary" className="flex-1" onClick={saveEdit}>
                  <FaSave className="mr-1" /> Save
                </GlassButton>
            </div>
          </GlassPanel>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDeleteConfirm(null)}>
          <GlassPanel className="max-w-sm w-full p-6 text-center" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Delete Customer?</h2>
            <p className="text-slate-600 mb-6 text-sm">This action cannot be undone. All related data will also be removed.</p>
            <div className="flex gap-3">
              <GlassButton variant="secondary" className="flex-1" onClick={() => setDeleteConfirm(null)}>Cancel</GlassButton>
              <GlassButton
                variant="primary"
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={() => handleDelete(deleteConfirm)}
              >
                Delete
              </GlassButton>
            </div>
          </GlassPanel>
        </div>
      )}
    </div>
  );
};

export default CustomersPage;
